import React from 'react';
import { Stack } from '@startuptools/ui/joy/Stack';
import { Typography } from '@startuptools/ui/joy/Typography';
import { Trans, useLingui } from '@lingui/react';
import { Switch } from '@startuptools/ui/joy/Switch';
import { FormControl } from '@startuptools/ui/joy/FormControl';
import { FormLabel } from '@startuptools/ui/joy/FormLabel';
import { GqlShareClass, GqlShareClassWithMinMax } from '../../../graphql/base-types.graphql';
import { Alert } from '@startuptools/ui/joy/Alert';
import { Button } from '@startuptools/ui/joy/Button';
import { Box } from '@startuptools/ui/joy/Box';
import { Table } from '@startuptools/ui/joy/Table';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@startuptools/ui/joy/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import { GqlSetShareClassInput, GqlValidatorStatus } from '../../../graphql/react-operations';
import { genericErrorToast, toast } from '@startuptools/ui/toast/use-toast';
import { ErrorDisplay } from '../../../components/error-display/ErrorDisplay';
import { useForm } from 'react-hook-form';
import { toNumber } from 'lodash-es';
import { Tooltip } from '@startuptools/ui/joy/Tooltip';
import { EditableTableCell } from './EditableTableCell';
import { useStepper } from '../../../components/steppers/common/StepperContext';

type FormData = Omit<GqlSetShareClassInput, 'id'>;

export const StepShareClasses = ({
  saveShareClass,
  companyShareClasses,
  shareClasses,
  minNumberOfShares = '0',
  maxNumberOfShares,
  validatorError,
  hideMinShares = false,
}: {
  saveShareClass: (data: FormData) => Promise<unknown>;
  companyShareClasses: GqlShareClass[];
  shareClasses: GqlShareClassWithMinMax[];
  minNumberOfShares: string | null | undefined;
  maxNumberOfShares?: string | null | undefined;
  validatorError: GqlValidatorStatus | null | undefined;
  hideMinShares?: boolean;
}) => {
  const { stepNext } = useStepper();
  const form = useForm<FormData>();
  const { i18n } = useLingui();
  const existingShareClasses = React.useMemo<(GqlShareClass & { min?: string; max?: string })[]>(() => {
    return [
      ...companyShareClasses.map(companyClass => {
        const entityClass = shareClasses.find(ec => ec.name === companyClass.name);
        return entityClass ? { ...companyClass, ...entityClass } : { ...companyClass, min: undefined, max: undefined };
      }),
      ...shareClasses.filter(sc => !companyShareClasses.some(csc => csc.name === sc.name)),
    ];
  }, [companyShareClasses, shareClasses]);

  const hasNewShareClass = shareClasses.some(
    sc => sc.name !== 'STAM' && !companyShareClasses.some(csc => csc.name === sc.name),
  );
  const [showCreateShareClass, setShowCreateShareClass] = React.useState(false);

  const shownShareClasses = React.useMemo<(Partial<GqlShareClassWithMinMax> & { draft?: true })[]>(() => {
    if (showCreateShareClass) {
      return [
        ...existingShareClasses,
        {
          name: undefined,
          voteWeight: '1',
          draft: true,
          min: minNumberOfShares ?? '',
          max: maxNumberOfShares ?? '',
        },
      ];
    }
    return existingShareClasses;
  }, [existingShareClasses, showCreateShareClass, minNumberOfShares, maxNumberOfShares]);

  const hasShareClasses = shownShareClasses.length > 0;
  const [showForm, setShowForm] = React.useState(hasShareClasses);
  const name = form.watch('name');

  const onSubmit = React.useCallback(
    async (sc: FormData) => {
      try {
        await saveShareClass({
          name: sc.name,
          voteWeight: sc.voteWeight,
          min: sc.min ?? '0',
          max: sc.max,
        });
        form.reset();
        setShowCreateShareClass(false);
      } catch {
        toast({
          title: <Trans id="Failed to save" />,
          description: <Trans id="Try again and contact support if the problem persists" />,
          severity: 'danger',
        });
      }
    },
    [form, saveShareClass, setShowCreateShareClass],
  );

  return (
    <Stack gap={3}>
      <Typography level="h4">
        <Trans id="Share classes" />
      </Typography>
      <Alert>
        {showForm ? (
          <Trans id="In this step you can set how many shares will be issued for each respective share class." />
        ) : (
          <Trans id="Your company has no share classes registered with Bolagsverket, if you are not planning to issue new share classes, you can skip this step." />
        )}
      </Alert>

      {hasNewShareClass && (
        <Alert variant="outlined" color="warning">
          <Trans id="You have added a new share class, please note that this requires an update to the articles of association." />
        </Alert>
      )}

      {validatorError?.hasError && <ErrorDisplay error={validatorError.error} severity={validatorError.severity} />}

      {!hasShareClasses && (
        <FormControl>
          <FormLabel>
            <Trans id="Will you issue multiple share classes?" />
          </FormLabel>
          <Switch
            sx={{ alignSelf: 'flex-start' }}
            startDecorator={
              <Typography level="body-sm">
                <Trans id="No" />
              </Typography>
            }
            endDecorator={
              <Typography level="body-sm">
                <Trans id="Yes" />
              </Typography>
            }
            checked={showForm}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setShowForm(event.target.checked)}
          />
        </FormControl>
      )}

      {showForm && (
        <>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Table sx={{ maxWidth: 784 }} size="sm" variant="outlined">
              <thead>
                <tr>
                  <th>
                    <Trans id="Name" />
                  </th>

                  <th>
                    <Trans id="Vote weight" />
                  </th>

                  {!hideMinShares && (
                    <th>
                      <Trans id="Minimum shares" />
                    </th>
                  )}

                  <th>
                    <Trans id="Maximum shares" />
                  </th>
                  <th align="right">
                    <Button
                      onClick={() => {
                        form.reset();
                        setShowCreateShareClass(true);
                      }}
                      endDecorator={<AddCircleIcon />}
                    >
                      <Trans id="Add" />
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shownShareClasses.map(sc => {
                  const isAdding = Boolean(sc.draft && showCreateShareClass);
                  const isEditing = name === sc.name || isAdding;
                  const isEditingAnother = name && name !== sc.name && !isAdding;
                  const toggleEdit = (show: boolean) => {
                    if (!show) {
                      return form.reset();
                    }
                    form.setValue('name', sc.name ?? '');
                    form.setValue('voteWeight', sc.voteWeight);
                    form.setValue('min', sc.min ?? minNumberOfShares);
                    form.setValue('max', sc.max ?? maxNumberOfShares);
                  };
                  return (
                    <tr key={sc.name}>
                      <td>
                        <EditableTableCell
                          name="name"
                          control={form.control}
                          value={sc.name}
                          isEditing={isAdding}
                          onEditToggle={toggleEdit}
                          autoFocus
                        />
                      </td>
                      <td>
                        <EditableTableCell
                          numeric
                          name="voteWeight"
                          control={form.control}
                          value={sc.voteWeight}
                          rules={{
                            required: i18n._('Vote weight is required'),
                          }}
                          isEditing={isAdding}
                          onEditToggle={toggleEdit}
                        />
                      </td>
                      {!hideMinShares && (
                        <td>
                          <EditableTableCell
                            numeric
                            decimalScale={0}
                            name="min"
                            control={form.control}
                            value={sc.min}
                            rules={{
                              min: {
                                value: 0,
                                message: i18n._(`Can't be less than 0`),
                              },
                            }}
                            isEditing={isEditing || isAdding}
                            onEditToggle={toggleEdit}
                            autoFocus={!isAdding}
                          />
                        </td>
                      )}
                      <td>
                        <EditableTableCell
                          numeric
                          decimalScale={0}
                          name="max"
                          control={form.control}
                          value={sc.max}
                          rules={{
                            required: i18n._('Maximum shares is required'),
                            min: {
                              value: 0,
                              message: i18n._(`Can't be less than 0`),
                            },
                            max: {
                              value: maxNumberOfShares ?? '',
                              message: i18n._(`Can't be greater than the maximum number of shares issued`),
                            },
                          }}
                          isEditing={isEditing || isAdding}
                          onEditToggle={toggleEdit}
                          sx={{
                            color: toNumber(sc.max) > toNumber(maxNumberOfShares) ? 'danger' : 'inherit',
                          }}
                        />
                      </td>
                      <td align="right">
                        {isEditing && (
                          <IconButton type={'submit'} color="success">
                            <SaveIcon />
                          </IconButton>
                        )}
                        {!isEditingAnother && (
                          <>
                            <Tooltip title={isEditing ? <Trans id="Cancel" /> : <Trans id="Edit" />}>
                              <IconButton
                                type={'button'}
                                onClick={() => {
                                  toggleEdit(!isEditing);
                                  setShowCreateShareClass(false);
                                }}
                                color={'neutral'}
                              >
                                {isEditing ? <CancelIcon /> : <EditIcon />}
                              </IconButton>
                            </Tooltip>
                            {sc.name && (
                              <Tooltip title={<Trans id="Clear share limits" />}>
                                <IconButton
                                  onClick={async () => {
                                    await onSubmit({
                                      name: sc.name!,
                                      voteWeight: sc.voteWeight,
                                      min: undefined,
                                      max: undefined,
                                    });
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </form>
        </>
      )}
      <Box>
        <Tooltip
          title={name || showCreateShareClass ? <Trans id="Finish editing the table before continuing" /> : null}
        >
          <span>
            <Button
              disabled={Boolean(name || showCreateShareClass)}
              onClick={async () => {
                if (!showForm) {
                  try {
                    await saveShareClass({
                      name: 'STAM',
                      voteWeight: '1',
                      min: minNumberOfShares,
                      max: maxNumberOfShares,
                    });
                    await stepNext();
                  } catch {
                    genericErrorToast();
                  }
                } else {
                  await stepNext();
                }
              }}
            >
              {hasShareClasses ? <Trans id="Continue" /> : <Trans id="Skip" />}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Stack>
  );
};
